import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronRight, Download, FileText, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useResults } from '@/state/useResults';
import { useSession } from '@/state/session';
import { useDemoStore } from '@/state/store';
import { formatRange, formatRangeShort } from '@/domain/time';
import { fmtNumber } from '@/domain/numbers';
import { presentText } from '@/lib/present';
import { SEVERITY_LABELS } from '@/lib/labels';
import { exportDeviationsCsv } from '@/lib/exports';
import { DEFAULT_THRESHOLDS } from '@/metrics/deviations';
import { EmptyState, PageSkeleton, Panel, PanelHeader, SkeletonTable } from '@/components/app/States';
import { DeviationStatusBadge, MachineRef, Note, SeverityBadge } from '@/components/app/Badges';
import { PageHeader } from '@/components/app/PageHeader';
import { SummaryCell, SummaryRow } from '@/components/app/Kpi';
import { ExampleDataNote, useDriftRefs } from '@/components/app/DriftUI';
import { DriftFilterBar, useDriftFilters } from '@/components/app/DriftFilters';
import { DEVIATION_VIEWS, DeviationList, DeviationViewChips, MeldAvvikSheet } from '@/components/app/Avvikssaker';
import { listDeviations } from '@/lib/driftApi';
import { cn } from '@/lib/utils';

/*
  Avvik har to tydelig adskilte faner med hver sin statusmodell:
  - Produksjonsvarsler: regelbaserte varsler fra produksjonsdataene i valgt periode (uendret).
  - HMS / RUH / Kvalitet: manuelt meldte RUH og kvalitetsavvik med egen saksgang (innmeldt → under behandling → lukket).
  Ingen av dem blandes med maskinfeil/service, som ligger under Maskiner.
*/

const HMS_KEYS = ['projectId', 'kind', 'devStatus', 'severity', 'responsibleId'];
const HMS_MORE = ['kind', 'devStatus', 'severity', 'responsibleId'];

/* ---------- Produksjonsvarsler (eksisterende, uendret logikk) ---------- */

function ProduksjonsvarslerPanel() {
  const r = useResults();
  const { role } = useSession();
  const store = useDemoStore();
  const navigate = useNavigate();
  const [tab, setTab] = useState('alle');
  const list = useMemo(() => {
    if (!r.ready) return [];
    return r.deviations.filter((d) => tab === 'alle' || store.statusOf(d.id) === tab);
  }, [r.ready, r.deviations, tab, store]);
  if (!r.ready) return <PageSkeleton />;
  const counts = { open: 0, reviewing: 0, resolved: 0 };
  const sev = { high: 0, medium: 0, info: 0 };
  for (const d of r.deviations) {
    counts[store.statusOf(d.id)]++;
    sev[d.severity]++;
  }
  const machinesById = new Map((r.dataset?.machines ?? []).map((m) => [m.machineId, m]));
  const dataQuality = r.deviations.filter((d) => d.ruleId === 'R3_DATA_QUALITY').length;

  return (
    <div className="space-y-6" data-testid="production-deviations">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-[14px] leading-6 text-[var(--hr-muted)]" data-testid="production-deviations-lead">
          {formatRange(r.fromDate, r.toDate)} mot {formatRange(r.compare.fromDate, r.compare.toDate)} · L/t +{DEFAULT_THRESHOLDS.dieselPerTonneIncreasePct} %, tomgang &gt;{DEFAULT_THRESHOLDS.idleSharePct} %, kildeforsinkelse
        </p>
        <Button variant="outline" size="sm" className="h-9 rounded-[10px] bg-white" onClick={() => exportDeviationsCsv(r, role, store.statusOf)} data-testid="export-deviations-csv-button">
          <Download className="mr-1.5 h-4 w-4" aria-hidden="true" /> CSV
        </Button>
      </div>

      {r.deviations.some((d) => d.suppresses?.length) && <Note tone="amber" data-testid="suppressed-note">Et datakvalitetsavvik har stanset effektivitetsvarsler (L/t) for perioden.</Note>}

      <SummaryRow data-testid="deviations-summary-strip">
        <SummaryCell label="Avvik i perioden" value={fmtNumber(r.deviations.length, 0)} meta={`${counts.open} åpne · ${counts.reviewing} under vurdering · ${counts.resolved} avklart`} testId="deviations-summary-total" />
        <SummaryCell label={SEVERITY_LABELS.high} value={fmtNumber(sev.high, 0)} meta="Krever oppfølging først" metaTone={sev.high ? 'amber' : undefined} testId="deviations-summary-high" />
        <SummaryCell label={SEVERITY_LABELS.medium} value={fmtNumber(sev.medium, 0)} meta="Undersøkes ved anledning" testId="deviations-summary-medium" />
        <SummaryCell label="Datakvalitet" value={fmtNumber(dataQuality, 0)} meta={dataQuality ? 'Manglende eller forsinket kilde' : 'Ingen kildeavvik i perioden'} metaTone={dataQuality ? 'amber' : undefined} testId="deviations-summary-quality" />
      </SummaryRow>

      <Panel data-testid="deviations-panel">
        <PanelHeader
          title={`${list.length} avvik${tab !== 'alle' ? ' med valgt status' : ''}`}
          description="Rangert etter alvorlighet."
          actions={
            <Tabs value={tab} onValueChange={setTab}>
              <TabsList className="h-auto min-h-9 max-w-full flex-wrap justify-start gap-0.5 rounded-[10px] bg-[var(--hr-inset)]">
                <TabsTrigger value="alle" className="rounded-[8px]" data-testid="deviations-tab-alle">Alle ({r.deviations.length})</TabsTrigger>
                <TabsTrigger value="open" className="rounded-[8px]" data-testid="deviations-tab-open">Åpne ({counts.open})</TabsTrigger>
                <TabsTrigger value="reviewing" className="rounded-[8px]" data-testid="deviations-tab-reviewing">Under vurdering ({counts.reviewing})</TabsTrigger>
                <TabsTrigger value="resolved" className="rounded-[8px]" data-testid="deviations-tab-resolved">Avklart ({counts.resolved})</TabsTrigger>
              </TabsList>
            </Tabs>
          }
        />
        {list.length === 0 ? (
          <div className="p-4">
            <EmptyState type="no_results" title={r.deviations.length === 0 ? 'Ingen avvik i perioden' : 'Ingen avvik med valgt status'} description={r.deviations.length === 0 ? 'Ingen regler slår ut for valgt periode. Prøv scenario «Flaskehals» eller en annen periode.' : 'Velg en annen statusfane.'} onReset={r.deviations.length === 0 ? r.reset : () => setTab('alle')} resetLabel={r.deviations.length === 0 ? 'Nullstill filtre' : 'Vis alle'} />
          </div>
        ) : (
          <div className="hr-scroll overflow-x-auto">
            <Table data-testid="deviations-table" className="[&_td]:py-3 [&_th]:h-10">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="pl-5">Alvorlighet</TableHead>
                  <TableHead>Regel</TableHead>
                  <TableHead>Berørt</TableHead>
                  <TableHead className="hidden md:table-cell">Periode</TableHead>
                  <TableHead>Observert</TableHead>
                  <TableHead className="hidden sm:table-cell">Status</TableHead>
                  <TableHead className="w-12 pr-5"><span className="sr-only">Åpne</span></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.map((d) => {
                  const to = r.linkTo(`/avvik/${encodeURIComponent(d.id)}`);
                  return (
                    <TableRow
                      key={d.id}
                      className="cursor-pointer"
                      data-testid={`deviation-row-${d.ruleId}-${d.scope.machineId ?? d.scope.sourceId ?? 'linje'}`}
                      onClick={(e) => {
                        if (e.target.closest('a')) return;
                        navigate(to);
                      }}
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && e.target === e.currentTarget && navigate(to)}
                    >
                      <TableCell className="pl-5"><SeverityBadge severity={d.severity} /></TableCell>
                      <TableCell className="font-medium">
                        <Link to={to} className="font-semibold text-[var(--hr-heading)] underline-offset-[3px] hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--hr-focus)]" data-testid={`deviation-link-${d.id}`}>
                          {d.ruleLabel}
                        </Link>
                      </TableCell>
                      <TableCell>
                        {d.scope.machineId ? (
                          <MachineRef machineId={d.scope.machineId} name={machinesById.get(d.scope.machineId)?.fictionalName} to={r.linkTo(`/maskiner/${d.scope.machineId}`)} />
                        ) : d.scope.sourceId ? (
                          <span className="text-sm">{presentText(d.scope.sourceId)}</span>
                        ) : (
                          <span className="text-sm text-[var(--hr-muted)]">Linje 1 (samlet)</span>
                        )}
                      </TableCell>
                      <TableCell className="hr-tabular hidden text-[var(--hr-muted)] md:table-cell">{formatRangeShort(d.period.fromDate, d.period.toDate)}</TableCell>
                      <TableCell className="hr-tabular text-sm">{presentText(d.observed)}</TableCell>
                      <TableCell className="hidden sm:table-cell"><DeviationStatusBadge status={store.statusOf(d.id)} /></TableCell>
                      <TableCell className="pr-5 text-right">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[var(--hr-muted-2)]" aria-hidden="true">
                          <ChevronRight className="h-4 w-4" />
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Panel>
    </div>
  );
}

/* ---------- HMS / RUH / Kvalitet (manuelle avvik med egen saksgang) ---------- */

/** Lite tall på fanen: åpne manuelle avvik (uavhengig av filtre). */
function useOpenDeviationCount(dep) {
  const [n, setN] = useState(null);
  useEffect(() => {
    let on = true;
    listDeviations({ open: 1, limit: 200 })
      .then((d) => on && setN(d.deviations.length))
      .catch(() => {});
    return () => {
      on = false;
    };
  }, [dep]);
  return n;
}

function HmsPanel() {
  const { refs } = useDriftRefs();
  const f = useDriftFilters({ keys: HMS_KEYS, defaultPreset: 'alle' });
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const view = DEVIATION_VIEWS.some((v) => v.id === params.get('visning')) ? params.get('visning') : 'apne';
  const ny = params.get('ny');
  const [data, setData] = useState(null);
  const [status, setStatus] = useState('loading');
  const queryKey = JSON.stringify(f.query);

  const setParam = useCallback(
    (key, value) => {
      const next = new URLSearchParams(params);
      if (value === null || value === undefined || value === '') next.delete(key);
      else next.set(key, value);
      setParams(next, { replace: true });
    },
    [params, setParams],
  );

  const load = useCallback(async () => {
    setStatus((s) => (s === 'ready' ? 'refreshing' : 'loading'));
    try {
      setData(await listDeviations(JSON.parse(queryKey)));
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  }, [queryKey]);

  useEffect(() => {
    load();
  }, [load]);

  const all = useMemo(() => data?.deviations ?? [], [data]);
  const viewDef = DEVIATION_VIEWS.find((v) => v.id === view) ?? DEVIATION_VIEWS[0];
  const list = useMemo(() => all.filter(viewDef.match), [all, viewDef]);
  const s = data?.summary;
  const from = { label: 'avvikslisten', to: `${location.pathname}${location.search}` };
  const reportLink = `/rapporter/avvik${f.carry({ periode: f.period.preset !== 'alle' ? f.period.preset : 'alle' })}`;
  const hasFilters = f.activeCount > 0 || f.period.preset !== 'alle';

  return (
    <div className="space-y-5" data-testid="hms-deviations">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-[720px] text-[14px] leading-6 text-[var(--hr-muted)]" data-testid="hms-lead">
          Manuelt meldte RUH (HMS-hendelser) og kvalitetsavvik med egen saksgang: innmeldt → under behandling → lukket. Lukking krever en kort konklusjon; gjenåpning bevarer historikken.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="outline" className="h-10 rounded-[10px] bg-white px-3 text-[14px] font-medium">
            <Link to={reportLink} data-testid="hms-report-link">
              <FileText className="mr-1.5 h-4 w-4 text-[var(--hr-muted)]" aria-hidden="true" /> Avviksrapport
            </Link>
          </Button>
          <Button className="h-10 rounded-[10px] bg-[var(--hr-heading)] px-4 text-[14px] font-semibold text-white shadow-none hover:bg-[var(--hr-navy)]" onClick={() => setParam('ny', '1')} data-testid="hms-new-button">
            <Plus className="mr-1.5 h-4 w-4" aria-hidden="true" /> Meld avvik
          </Button>
        </div>
      </div>

      <ExampleDataNote details="Fiktive hendelser fra grøft, knuseverk, kai og masser/rørdeler. Ingen melding sendes til noen, og dette er en arbeidsflyt-demonstrasjon – ikke en påstand om HMS-etterlevelse eller en autentisert revisjonslogg. Personer, prosjekter og kategorier kommer fra registrene i Innstillinger.">
        Fiktive RUH og kvalitetsavvik – ingen melding sendes til noen.
      </ExampleDataNote>

      {status === 'loading' && !data && <SkeletonTable rows={6} />}
      {status === 'error' && !data && (
        <Note tone="amber" data-testid="hms-load-error" role="alert">
          Kunne ikke hente avvikene.{' '}
          <Button variant="link" className="h-auto px-1" onClick={load} data-testid="hms-load-retry">
            Prøv igjen
          </Button>
        </Note>
      )}

      {data && (
        <>
          <SummaryRow data-testid="hms-summary-strip" className={cn(status === 'refreshing' && 'opacity-70 transition-opacity duration-150')}>
            <SummaryCell label="Åpne avvik" value={fmtNumber(s.open, 0)} meta={`${s.openByKind?.ruh ?? 0} RUH · ${s.openByKind?.kvalitet ?? 0} kvalitet`} onClick={() => setParam('visning', 'apne')} testId="hms-summary-open" />
            <SummaryCell label="Forfalt frist" value={fmtNumber(s.overdue, 0)} meta={s.overdue ? 'Åpne avvik med passert frist' : 'Ingen passerte frister'} metaTone={s.overdue ? 'amber' : undefined} onClick={() => setParam('visning', 'forfalt')} testId="hms-summary-overdue" />
            <SummaryCell label="Åpne tiltak" value={fmtNumber(s.actionsOpen, 0)} meta={s.actionsOverdue ? `${s.actionsOverdue} ${s.actionsOverdue === 1 ? 'forfalt tiltak' : 'forfalte tiltak'}` : 'Ingen forfalte tiltak'} metaTone={s.actionsOverdue ? 'amber' : undefined} onClick={() => setParam('visning', 'forfalt')} testId="hms-summary-actions" />
            <SummaryCell label="Lukket" value={fmtNumber(s.closed, 0)} meta={`${s.total} avvik i utvalget`} onClick={() => setParam('visning', 'lukket')} testId="hms-summary-closed" />
          </SummaryRow>

          <Panel className="min-w-0">
            <DriftFilterBar f={f} refs={refs} more={HMS_MORE} testPrefix="hms">
              <DeviationViewChips value={view} onChange={(v) => setParam('visning', v === 'apne' ? null : v)} deviations={all} testId="hms-view" />
            </DriftFilterBar>
            <div className={cn(status === 'refreshing' && 'opacity-70 transition-opacity duration-150')}>
              {all.length === 0 ? (
                <div className="p-5">
                  <EmptyState title={hasFilters ? 'Ingen avvik i utvalget' : 'Ingen avvik meldt'} description={hasFilters ? 'Endre periode eller filtre.' : 'Meld et RUH eller kvalitetsavvik for å komme i gang.'} onReset={hasFilters ? f.reset : undefined} action={<Button size="sm" onClick={() => setParam('ny', '1')} data-testid="hms-empty-new">Meld avvik</Button>} />
                </div>
              ) : (
                <DeviationList deviations={list} testId="hms-list" from={from} emptyText={view === 'apne' ? 'Ingen åpne avvik i utvalget.' : view === 'forfalt' ? 'Ingen forfalte frister eller tiltak i utvalget.' : view === 'lukket' ? 'Ingen lukkede avvik i utvalget.' : 'Ingen avvik.'} />
              )}
            </div>
            <div className="border-t border-[var(--hr-hairline)] px-5 py-3 text-[13px] text-[var(--hr-muted)]">
              Sortert: åpne først, deretter passert frist og alvorlighet. Status og tall er de samme som i avviksrapporten.
            </div>
          </Panel>
        </>
      )}

      <MeldAvvikSheet
        open={!!ny}
        onOpenChange={(o) => !o && setParam('ny', null)}
        initialKind={ny === 'ruh' || ny === 'kvalitet' ? ny : ''}
        initialProjectId={f.values.projectId !== 'alle' ? f.values.projectId : ''}
        onSaved={load}
        returnTo={from}
      />
    </div>
  );
}

export default function AvvikPage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('fane') === 'hms' ? 'hms' : 'produksjon';
  const openCount = useOpenDeviationCount(tab);
  /** Produksjonsfilteret (fra/til/sml/omfang …) og HMS-filtrene (periode/projectId …) har ulike nøkler og bevares hver for seg. */
  const setTab = (v) => {
    const next = new URLSearchParams(params);
    if (v === 'hms') next.set('fane', 'hms');
    else {
      next.delete('fane');
      for (const k of ['visning', 'ny', 'periode', 'from', 'to', ...HMS_KEYS]) next.delete(k);
    }
    setParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Avvik" lead={tab === 'hms' ? 'HMS, RUH og kvalitetsavvik meldt manuelt – egen saksgang, tiltak og rapport.' : 'Regelbaserte produksjonsvarsler fra produksjonsdataene i valgt periode.'} />

      {/* To faner med hver sin statusmodell – produksjonsvarsler og manuelle HMS/kvalitetsavvik blandes aldri. */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="h-10 rounded-[10px] bg-[var(--hr-surface-2)] p-1" data-testid="avvik-tabs">
          <TabsTrigger value="produksjon" className="h-8 rounded-[8px] px-4 text-[14px] data-[state=active]:bg-white data-[state=active]:shadow-[var(--hr-shadow-xs)]" data-testid="avvik-tab-produksjon">
            Produksjonsvarsler
          </TabsTrigger>
          <TabsTrigger value="hms" className="h-8 rounded-[8px] px-4 text-[14px] data-[state=active]:bg-white data-[state=active]:shadow-[var(--hr-shadow-xs)]" data-testid="avvik-tab-hms">
            HMS / RUH / Kvalitet
            {openCount ? (
              <span className="hr-tabular ml-1.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-[rgba(29,40,86,0.08)] px-1.5 text-[12px] font-semibold text-[var(--hr-navy)]" title={`${openCount} åpne avvik`} data-testid="avvik-tab-hms-count">
                {openCount}
              </span>
            ) : null}
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {tab === 'hms' ? <HmsPanel /> : <ProduksjonsvarslerPanel />}
    </div>
  );
}
